<script setup lang="ts">
import IconCart from '~icons/mdi/cart'
import IconHeart from '~icons/mdi/heart'
import IconHistory from '~icons/mdi/history'
import IconLogout from '~icons/mdi/logout'
import IconMenu from '~icons/mdi/menu'

const constants = useConstants()
const userStore = useUserStore()
const cartStore = useCart()

const localePath = useLocalePath()

const mobileMenuOpen = ref(false)

const categoryMenuItems = useCategoryMenuItems()

interface MenuItem { title?: string, to?: string, i18nKey?: string, prependIcon?: any, class?: string, onClick?: (() => unknown), activeQueryParam?: string, navId?: string }
const menuItems = ref<MenuItem[]>([])

function clickOnMenuItem(item: MenuItem) {
  mobileMenuOpen.value = false
  if (item.onClick)
    item.onClick()
}

watch([() => userStore.data, categoryMenuItems], () => {
  menuItems.value = []

  if (userStore.data?.clinicChain) {
    menuItems.value.push({
      title: userStore.data.clinicChain.name,
      to: localePath('/clinic-chain'),
      activeQueryParam: constants.QUERY_PARAMS.categories,
      navId: 'mobile_menu_clinic_chain',
    })
  }

  categoryMenuItems.value.forEach(cat => menuItems.value.push({ title: cat.label, to: cat.to, navId: cat.navId }))

  menuItems.value.push({
    to: localePath('/suppliers'),
    i18nKey: 'layout.menuSecondary.suppliers',
    navId: 'mobile_menu_suppliers',
  })

  menuItems.value.push({
    to: localePath('/campaigns'),
    i18nKey: 'layout.menuSecondary.campaigns',
    navId: 'mobile_menu_campaigns',
  })

  menuItems.value.push({
    i18nKey: 'logout',
    prependIcon: IconLogout,
    onClick: () => { userStore.logout() },
    navId: 'mobile_menu_logout',
  })
}, { immediate: true })
</script>

<template>
  <v-bottom-navigation
    bg-color="red-400"
    elevation="0"
    grow
    class="w-100 mobile-menu-wrapper"
  >
    <v-bottom-sheet v-model="mobileMenuOpen">
      <template #activator="{ props }">
        <v-btn v-bind="props" data-track-id="mobile_menu_toggle_menu" class="pa-0">
          <v-icon :icon="IconMenu" />
          {{ $t('menu') }}
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-for="(item, index) in menuItems"
          :key="index"
          :title="item.i18nKey ? $t(item.i18nKey) : item.title"
          :to="item.to ? item.to : undefined"
          :prepend-icon="item.prependIcon ? item.prependIcon : undefined"
          :class="item.class ? item.class : undefined"
          :data-nav-id="item.navId"
          @click="clickOnMenuItem(item)"
        />
      </v-list>
    </v-bottom-sheet>
    <v-btn :to="localePath('/order-history')" class="pa-0" data-nav-id="nav_order_history">
      <v-icon :icon="IconHistory" />
      {{ $t('orders') }}
    </v-btn>

    <v-btn :to="localePath('/product-favorites')" class="pa-0" data-nav-id="nav_favorites">
      <v-icon :icon="IconHeart" />
      {{ $t('favorites') }}
    </v-btn>
    <v-btn
      v-if="cartStore.totalAmount"
      :to="localePath('/cart')"
      class="pa-0"
      data-nav-id="bottom_nav_cart"
    >
      <v-badge
        :max="99"
        :content="cartStore.totalAmount"
        offset-x="-5"
        color="heiland-light-blue"
      >
        <v-icon :icon="IconCart" />
      </v-badge>
      {{ $t('cart.title') }}
    </v-btn>
    <v-btn
      v-else
      color="white"
      :to="localePath('/cart')"
      data-nav-id="bottom_nav_cart"
    >
      <v-icon :icon="IconCart" />
      {{ $t('cart.title') }}
    </v-btn>
  </v-bottom-navigation>
</template>
